import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { initials, PRIORITY_LABEL, STATUS_LABEL, type TicketStatus } from '@helpdesk/shared';
import { postJson } from '../api/client.ts';
import { activeSitesQuery, meQuery, teamsQuery, workersQuery } from '../api/queries.ts';
import type { TicketDetail } from '../api/types.ts';
import { Button, errorMessage, ErrorNote, Field, Modal, Notice, Spinner, Textarea } from './ui.tsx';

/**
 * Zuweisung wie in InvGate: Jedes Ticket gehört einem Team („Help Desk“) und
 * optional einer Person darin. „Übernehmen“ weist mir zu, „Zuweisen“ öffnet den
 * Dialog mit Team, Person und interner Notiz für die Übergabe.
 */

/** Wer ist gerade am Zug? (InvGate-Spalte „Waiting for“) */
export function waitingFor(status: TicketStatus, hasAssignee: boolean): { label: string; tone: 'red' | 'steel' | 'warn' | 'ok' | 'muted' } {
  switch (status) {
    case 'new':
      return hasAssignee ? { label: 'Bearbeiter', tone: 'steel' } : { label: 'Zuweisung', tone: 'red' };
    case 'accepted':
    case 'in_progress':
      return hasAssignee ? { label: 'Bearbeiter', tone: 'steel' } : { label: 'Zuweisung', tone: 'red' };
    case 'waiting_part':
      return { label: 'Ersatzteil', tone: 'warn' };
    case 'waiting_external':
      return { label: 'Dienstleister', tone: 'warn' };
    case 'waiting_reporter':
      return { label: 'Melder', tone: 'warn' };
    case 'resolved':
      return { label: 'Bestätigung Melder', tone: 'ok' };
    default:
      return { label: 'niemand', tone: 'muted' };
  }
}

const waitingTone = {
  red: 'bg-arnold-red',
  steel: 'bg-steel-700',
  warn: 'bg-warn',
  ok: 'bg-ok',
  muted: 'bg-steel-300',
} as const;

export function WaitingFor({ status, hasAssignee }: { status: TicketStatus; hasAssignee: boolean }) {
  const w = waitingFor(status, hasAssignee);
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] whitespace-nowrap text-steel-800">
      <span className={`inline-block h-2 w-2 shrink-0 ${waitingTone[w.tone]}`} aria-hidden />
      {w.label}
    </span>
  );
}

/** Kürzel im Quadrat (Arnold: scharfe Ecken). Teams in Stahl, Personen in Rot. */
export function Avatar({ name, kind = 'person', size = 28 }: { name: string; kind?: 'person' | 'team' | 'none'; size?: number }) {
  const tone = kind === 'team' ? 'bg-steel-700 text-white' : kind === 'none' ? 'border border-dashed border-steel-400 bg-white text-steel-500' : 'bg-arnold-red text-white';
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center font-display font-bold ${tone}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden
    >
      {kind === 'none' ? '?' : initials(name) || '?'}
    </span>
  );
}

export type AssignTarget = {
  id: string;
  version: number;
  display: string;
  siteId: string;
  teamId: string | null;
  assigneeId: string | null;
  status: TicketStatus;
  /** Nur von der Ticketseite: Die Liste friert ihre Auswahl ein, dort weicht unter dem Dialog nichts ab. */
  detail?: TicketDetail;
};

/** „Übernehmen“: mir zuweisen. Liefert die neue Version. */
export async function takeTicket(t: Pick<AssignTarget, 'id' | 'version'>): Promise<{ version: number; status: TicketStatus }> {
  return postJson(`/api/tickets/${t.id}/take`, { version: t.version });
}

/** Der Dialog verdeckt die Seite, und Schließen verwirft die Eingaben: Was sich geändert hat, muss im Dialog stehen. */
export function ticketChanges(seen: TicketDetail, now: TicketDetail): string[] {
  const changes: string[] = [];
  if (now.status !== seen.status) changes.push(`Status: ${STATUS_LABEL[now.status]}`);
  if (now.priority !== seen.priority) changes.push(`Priorität: ${PRIORITY_LABEL[now.priority]}`);
  if (now.team?.id !== seen.team?.id) changes.push(`Team: ${now.team?.name ?? '-'}`);
  if (now.assignee?.id !== seen.assignee?.id) changes.push(`Bearbeiter: ${now.assignee?.displayName ?? 'Nicht zugewiesen'}`);
  if (now.machine?.id !== seen.machine?.id) changes.push(`Maschine: ${now.machine?.name ?? '-'}`);
  if (now.lockedOut !== seen.lockedOut) changes.push(now.lockedOut ? 'Maschine gesichert' : 'Sicherung aufgehoben');
  return changes;
}

/**
 * Steht im Dialogfuß, sobald das Ticket vom Stand beim Öffnen abweicht. Der Dialog hält das Absenden
 * gesperrt, bis `onSeen` den neuen Stand übernimmt: Sonst ginge ein Klick auf ungesehener Grundlage durch.
 */
export function TicketChangedNote({ changes = [], onSeen }: { changes?: string[]; onSeen: () => void }) {
  return (
    <>
      <div role="alert" className="w-full">
        <Notice tone="warn">
          Das Ticket wurde inzwischen geändert. Bitte den Stand prüfen.
          {changes.length ? <strong className="mt-1 block">{changes.join(' · ')}</strong> : null}
        </Notice>
      </div>
      <Button variant="secondary" onClick={onSeen}>
        Änderungen gesehen
      </Button>
    </>
  );
}

/**
 * Dialog „Zuweisen“ für ein oder mehrere Tickets. Team zuerst (Pflicht), dann
 * optional eine Person aus dem Team. Die Notiz ist intern – Melder sehen sie nie.
 */
export function AssignDialog({ tickets, onClose, onDone }: { tickets: AssignTarget[]; onClose: () => void; onDone: () => Promise<void> | void }) {
  const me = useQuery(meQuery);
  const teams = useQuery(teamsQuery);
  const sites = useQuery(activeSitesQuery);
  const first = tickets[0];
  const siteIds = useMemo(() => [...new Set(tickets.map((t) => t.siteId))], [tickets]);
  const [teamId, setTeamId] = useState(first?.teamId ?? '');
  const [person, setPerson] = useState(tickets.length === 1 ? (first?.assigneeId ?? '') : '');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Die Ticketseite reicht nach jedem Refetch die neue Version herein, gesendet wird die vom Öffnen.
  const [seen, setSeen] = useState(tickets);
  const changed = !busy && tickets.some((t, i) => t.version !== seen[i]?.version);
  const was = seen[0];

  // Teams der Werke dieser Tickets, auch wenn das Werk stillgelegt ist; die Leitung sieht zusätzlich alle aktiven (Werk wechseln geht nur ohne Maschine).
  const options = (teams.data ?? []).filter((t) => t.isActive && (siteIds.includes(t.siteId) || (me.data?.user.role === 'lead' && sites.data?.some((s) => s.id === t.siteId))));
  const bySite = new Map<string, typeof options>();
  for (const t of options) bySite.set(t.siteName, [...(bySite.get(t.siteName) ?? []), t]);
  const team = options.find((t) => t.id === teamId);

  useEffect(() => {
    if (!teamId && options.length) {
      const preferred = options.find((t) => t.isDefault && siteIds.includes(t.siteId)) ?? options[0]!;
      setTeamId(preferred.id);
    }
  }, [teamId, options, siteIds]);
  useEffect(() => {
    // Person muss zum Team gehören – sonst „nur Team“.
    if (team && person && !team.members.some((m) => m.id === person)) setPerson('');
  }, [team, person]);

  const needsPerson = tickets.some((t) => t.status === 'in_progress' || t.status.startsWith('waiting_'));
  const meId = me.data?.user.id;

  const acceptChanges = () => {
    // Eine unberührte Auswahl zeigt noch den alten Stand, „Zuweisen“ schriebe die Zuweisung des Kollegen zurück.
    if (was && first && teamId === (was.teamId ?? '') && person === (was.assigneeId ?? '')) {
      setTeamId(first.teamId ?? '');
      setPerson(first.assigneeId ?? '');
    }
    setSeen(tickets);
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    const failed: string[] = [];
    for (const t of seen) {
      try {
        await postJson(`/api/tickets/${t.id}/reassign`, {
          version: t.version,
          teamId,
          assigneeUserId: person || null,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        });
      } catch (err) {
        failed.push(`${t.display}: ${errorMessage(err)}`);
      }
    }
    setError(failed.length ? new Error(failed.join('\n')) : null);
    await onDone();
    setBusy(false);
    if (!failed.length) onClose();
  };

  return (
    <Modal
      open
      title={tickets.length === 1 ? `${first?.display} zuweisen` : `${tickets.length} Tickets zuweisen`}
      onClose={onClose}
      error={changed ? null : error}
      footer={
        <>
          {changed ? <TicketChangedNote changes={was?.detail && first?.detail ? ticketChanges(was.detail, first.detail) : []} onSeen={acceptChanges} /> : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={busy || changed || !teamId || (needsPerson && !person)} onClick={() => void submit()}>
            {busy ? 'Wird zugewiesen …' : 'Zuweisen'}
          </Button>
        </>
      }
    >
      {teams.isPending ? <Spinner label="Teams werden geladen …" /> : null}
      {teams.isError ? <ErrorNote error={teams.error} /> : null}
      <Field label="Team" required>
        <select className="ad-select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          {[...bySite.entries()].map(([siteName, list]) => (
            <optgroup key={siteName} label={siteName}>
              {list.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? ' (Standard)' : ''} · {t.openCount} offen
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>

      <fieldset className="space-y-1.5">
        <legend className="ad-label">Person {needsPerson ? <span className="text-arnold-red">*</span> : null}</legend>
        <PersonOption checked={person === ''} onSelect={() => setPerson('')} name={team?.name ?? 'Team'} kind="team" label="Nur ans Team" hint="Alle im Team sehen es unter „Nicht zugewiesen“." disabled={needsPerson} />
        {(team?.members ?? []).map((m) => (
          <PersonOption key={m.id} checked={person === m.id} onSelect={() => setPerson(m.id)} name={m.displayName} label={m.id === meId ? `${m.displayName} (ich)` : m.displayName} hint={m.role === 'lead' ? 'Leitung' : 'Instandhalter'} />
        ))}
        {team && !team.members.length ? <Notice>Dieses Team hat noch keine Mitglieder. Bitte unter „Teams“ pflegen.</Notice> : null}
      </fieldset>

      <Textarea
        label="Interne Notiz zur Übergabe"
        hint="Nur für die Instandhaltung sichtbar, erscheint in der Zeitleiste."
        value={comment}
        onChange={setComment}
        rows={3}
        maxLength={2000}
        placeholder="z. B. „Fehlerbild klingt elektrisch, bitte Schaltschrank prüfen.“"
      />
      {needsPerson ? <Notice>Tickets in Arbeit oder wartend brauchen eine Person.</Notice> : null}
    </Modal>
  );
}

function PersonOption({
  checked,
  onSelect,
  name,
  label,
  hint,
  kind = 'person',
  disabled = false,
}: {
  checked: boolean;
  onSelect: () => void;
  name: string;
  label: string;
  hint?: string;
  kind?: 'person' | 'team';
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 border px-3 py-2 ${checked ? 'border-arnold-red bg-arnold-red-tint' : 'border-[var(--border-default)] hover:bg-steel-50'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <input type="radio" name="assign-person" checked={checked} disabled={disabled} onChange={onSelect} className="h-[18px] w-[18px] accent-[var(--color-arnold-red)]" />
      <Avatar name={name} kind={kind} />
      <span className="min-w-0">
        <span className="block truncate text-[15px] text-steel-900">{label}</span>
        {hint ? <span className="block text-[13px] text-[var(--text-muted)]">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * Kollegen hinzuziehen – auch aus dem anderen Werk (InvGate: Collaborators).
 * Hinzugezogene sehen das Ticket, kommentieren und werden benachrichtigt;
 * Zuweisung und Annahme bleiben beim Werk des Tickets.
 */
export function CollaboratorDialog({
  ticket,
  exclude,
  onClose,
  onDone,
}: {
  ticket: { id: string; version: number; display: string; siteId: string };
  exclude: string[];
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const workers = useQuery(workersQuery());
  const sites = useQuery(activeSitesQuery);
  const [userId, setUserId] = useState('');
  const [comment, setComment] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [seenVersion, setSeenVersion] = useState(ticket.version);
  const changed = !busy && ticket.version !== seenVersion;

  const needle = filter.trim().toLowerCase();
  const candidates = (workers.data ?? []).filter((w) => !exclude.includes(w.id) && (!needle || w.displayName.toLowerCase().includes(needle)));
  // Erst das andere Werk – dafür ist die Funktion vor allem da.
  const siteNames = (ids: string[]) => (sites.data ?? []).filter((s) => ids.includes(s.id)).map((s) => s.name);
  const groups = new Map<string, typeof candidates>();
  for (const w of candidates) {
    const key = siteNames(w.siteIds).join(' und ') || 'Ohne Werk';
    groups.set(key, [...(groups.get(key) ?? []), w]);
  }
  const ordered = [...groups.entries()].sort(([a], [b]) => {
    const own = siteNames([ticket.siteId])[0] ?? '';
    return (a.includes(own) ? 1 : 0) - (b.includes(own) ? 1 : 0) || a.localeCompare(b);
  });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson(`/api/tickets/${ticket.id}/collaborators`, { version: seenVersion, userId, ...(comment.trim() ? { comment: comment.trim() } : {}) });
      await onDone();
      onClose();
    } catch (err) {
      setError(err);
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Kollegen zu ${ticket.display} hinzuziehen`}
      onClose={onClose}
      error={changed ? null : error}
      footer={
        <>
          {changed ? (
            <TicketChangedNote
              onSeen={() => {
                setSeenVersion(ticket.version);
                setError(null);
              }}
            />
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={busy || changed || !userId} onClick={() => void submit()}>
            Hinzuziehen
          </Button>
        </>
      }
    >
      <p className="text-[15px] text-steel-700">
        Für Hilfe aus dem anderen Werk oder eine zweite Meinung. Die Person sieht das Ticket, kann kommentieren und Fotos anhängen und wird benachrichtigt. Zuweisung und Annahme bleiben beim Werk des Tickets.
      </p>
      <input className="ad-input" placeholder="Name suchen" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Name suchen" />
      {workers.isPending ? <Spinner label="Kollegen werden geladen …" /> : null}
      <div className="max-h-[320px] space-y-3 overflow-auto">
        {ordered.map(([group, list]) => (
          <fieldset key={group} className="space-y-1.5">
            <legend className="ad-caption mb-1">{group}</legend>
            {list.map((w) => (
              <PersonOption
                key={w.id}
                checked={userId === w.id}
                onSelect={() => setUserId(w.id)}
                name={w.displayName}
                label={w.displayName}
                hint={w.role === 'lead' ? 'Leitung' : 'Instandhalter'}
              />
            ))}
          </fieldset>
        ))}
        {!workers.isPending && !candidates.length ? <p className="text-[15px] text-[var(--text-muted)]">Niemand gefunden.</p> : null}
      </div>
      <Textarea
        label="Notiz für die Person (optional)"
        hint="Erscheint intern in der Zeitleiste."
        value={comment}
        onChange={setComment}
        rows={2}
        maxLength={1000}
        placeholder="z. B. „Kennst du den Fehler 17 vom Laser in Steinbach?“"
      />
    </Modal>
  );
}
